import { Bell } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CountBadge } from '@/components/ui/CountBadge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { useAuth } from '@/contexts/AuthContext';
import {
  useMarkNotificationsSeen,
  useNotificationCount,
} from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { NotificationCenterPanel } from './NotificationCenterPanel';

interface NotificationBellProps {
  isCollapsed?: boolean;
}

/**
 * Notification center trigger for coordinators (ADMIN only). Rendered as a
 * sidebar nav-tab button; the count badge is green when there are items the user
 * hasn't seen, muted once seen. Opening the center marks open items seen
 * (de-bolds) — it never resolves them.
 */
export function NotificationBell({ isCollapsed }: NotificationBellProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [open, setOpen] = useState(false);
  const { data } = useNotificationCount({ enabled: isAdmin });
  const markSeen = useMarkNotificationsSeen();

  if (!isAdmin) return null;

  const openCount = data?.open ?? 0;
  const hasNew = (data?.newSinceSeen ?? 0) > 0;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && hasNew) {
      markSeen.mutate();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={open ? 'secondary' : 'ghost'}
          size={isCollapsed ? 'icon' : 'md'}
          className={cn(
            'relative',
            isCollapsed
              ? 'w-full justify-center'
              : 'w-full justify-start gap-3 text-caption-label'
          )}
          aria-label={`Notifications${openCount > 0 ? `, ${openCount} open` : ''}`}
        >
          <Bell />
          {!isCollapsed && <span>Notifications</span>}
          <CountBadge
            count={openCount}
            className={cn(
              isCollapsed
                ? 'absolute -top-1 -right-1 h-4 min-w-4 px-1'
                : 'ml-auto',
              hasNew && 'bg-primary text-primary-foreground'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-96 p-0">
        <NotificationCenterPanel onNavigated={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
